import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create Auth Context
const AuthContext = createContext();

// Language Context
const LanguageContext = createContext();

// Multi-language support
const translations = {
  en: {
    home: "Home",
    about: "About",
    technology: "Technology",
    services: "Services",
    successStories: "Success Stories",
    fundraising: "Fundraising",
    blog: "Blog",
    team: "Team",
    partnerships: "Partnerships",
    contact: "Contact",
    login: "Login",
    register: "Register",
    dashboard: "Dashboard",
    welcome: "Welcome",
    logout: "Logout",
    startRecovery: "Start Recovery",
    supportMission: "Support Our Mission",
    donateNow: "Donate Now",
    readMore: "Read More",
    learnMore: "Learn More",
    contactUs: "Contact Us"
  },
  es: {
    home: "Inicio",
    about: "Acerca",
    technology: "Tecnología",
    services: "Servicios",
    successStories: "Historias de Éxito",
    fundraising: "Recaudación",
    blog: "Blog",
    team: "Equipo",
    partnerships: "Asociaciones",
    contact: "Contacto",
    login: "Iniciar Sesión",
    register: "Registrarse",
    dashboard: "Panel",
    welcome: "Bienvenido",
    logout: "Cerrar Sesión",
    startRecovery: "Iniciar Recuperación",
    supportMission: "Apoyar Nuestra Misión",
    donateNow: "Donar Ahora",
    readMore: "Leer Más",
    learnMore: "Aprender Más",
    contactUs: "Contáctanos"
  },
  zh: {
    home: "首页",
    about: "关于",
    technology: "技术",
    services: "服务",
    successStories: "成功案例",
    fundraising: "筹款",
    blog: "博客",
    team: "团队",
    partnerships: "合作伙伴",
    contact: "联系",
    login: "登录",
    register: "注册",
    dashboard: "仪表板",
    welcome: "欢迎",
    logout: "登出",
    startRecovery: "开始恢复",
    supportMission: "支持我们的使命",
    donateNow: "立即捐赠",
    readMore: "阅读更多",
    learnMore: "了解更多",
    contactUs: "联系我们"
  }
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      fetchNotifications();
    } catch (error) {
      console.error('Error fetching user info:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      await fetchUserInfo();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, full_name, referral_code = null) => {
    try {
      const { language } = useLanguage();
      await axios.post(`${API}/auth/register`, { 
        email, 
        password, 
        full_name, 
        language,
        referred_by: referral_code 
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setNotifications([]);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, notifications, fetchNotifications }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Top Progress Bar Component
const TopProgressBar = () => {
  const [stats, setStats] = useState({
    total_raised: 0,
    goal_amount: 500000,
    progress_percentage: 0,
    total_donors: 0
  });
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    fetchStats();
    // Update every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/donations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching fundraising stats:', error);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 px-4 relative">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🎯</span>
              <div>
                <div className="text-sm font-medium">Funds Raised</div>
                <div className="text-lg font-bold">
                  ${stats.total_raised.toLocaleString()} of ${stats.goal_amount.toLocaleString()}
                </div>
              </div>
            </div>
            
            <div className="flex-1 max-w-md mx-4">
              <div className="bg-white/20 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-white h-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.min(stats.progress_percentage, 100)}%` }}
                >
                  <div className="h-full bg-gradient-to-r from-green-400 to-green-600 animate-pulse"></div>
                </div>
              </div>
              <div className="text-center mt-1">
                <span className="text-sm font-medium">
                  {stats.progress_percentage.toFixed(1)}% Complete • {stats.total_donors} Contributors
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => document.getElementById('enhanced-fundraising').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Donate Now
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="text-white/80 hover:text-white text-lg"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Fundraising Progress Component
const FundraisingProgress = ({ showFull = false }) => {
  const [stats, setStats] = useState({
    total_raised: 0,
    goal_amount: 500000,
    progress_percentage: 0,
    total_donors: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/donations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching fundraising stats:', error);
    }
  };

  if (!showFull) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
        <div className="text-center mb-2">
          <div className="text-sm font-medium text-gray-700">Fundraising Progress</div>
          <div className="text-lg font-bold text-blue-600">
            ${stats.total_raised.toLocaleString()} / ${stats.goal_amount.toLocaleString()}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(stats.progress_percentage, 100)}%` }}
          ></div>
        </div>
        <div className="text-xs text-gray-600 text-center mt-1">
          {stats.progress_percentage.toFixed(1)}% • {stats.total_donors} contributors
        </div>
      </div>
    );
  }

  return null;
};

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', content: 'Hello! I\'m here to help you with cryptocurrency recovery questions. How can I assist you today?' }
  ]);
  const [inputValue, setInputValue] = useState('');

  const faqResponses = {
    'recovery': 'Our recovery process uses advanced optical computing to analyze cryptographic patterns. We can help with password recovery, seed phrase restoration, and private key recovery.',
    'secure': 'Yes, we use military-grade encryption and air-gapped systems. Your data is never stored permanently and is destroyed after successful recovery.',
    'time': 'Recovery time varies: simple passwords take 1-3 days, complex seed phrases take 5-10 days. Our optical computing systems work 24/7.',
    'success': 'Our success rate is over 85% for cases where we have sufficient information. The more details you provide, the higher the success rate.',
    'cost': 'Costs vary by complexity. We offer transparent pricing and only charge upon successful recovery. Contact us for a free consultation.',
    'partial': 'Yes! Our optical computing excels at partial seed phrase recovery. Even remembering a few words can be sufficient for our systems.'
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = { type: 'user', content: inputValue };
    setMessages(prev => [...prev, userMessage]);

    // Simple keyword matching for responses
    const input = inputValue.toLowerCase();
    let response = "I understand you're asking about cryptocurrency recovery. For specific technical questions, please contact our support team. Here are some common topics I can help with: recovery process, security, timeframes, success rates, costs, and partial seed phrases.";

    for (const [key, value] of Object.entries(faqResponses)) {
      if (input.includes(key)) {
        response = value;
        break;
      }
    }

    const botMessage = { type: 'bot', content: response };
    setTimeout(() => {
      setMessages(prev => [...prev, botMessage]);
    }, 1000);

    setInputValue('');
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {/* Chatbot Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-80 bg-white rounded-lg shadow-xl border z-50">
          <div className="bg-blue-600 text-white p-4 rounded-t-lg">
            <h3 className="font-semibold">OpticalRecover Support</h3>
            <p className="text-sm opacity-90">Ask me anything about crypto recovery</p>
          </div>
          
          <div className="h-64 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div key={index} className={`${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block p-2 rounded-lg max-w-xs ${
                  message.type === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {message.content}
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSendMessage}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const { language, changeLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl font-bold text-blue-600">OpticalRecover</span>
            </div>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-baseline space-x-4">
              <button onClick={() => scrollToSection('home')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('home')}</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('about')}</button>
              <button onClick={() => scrollToSection('technology')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('technology')}</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('services')}</button>
              <button onClick={() => scrollToSection('success-stories')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('successStories')}</button>
              <button onClick={() => scrollToSection('enhanced-fundraising')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('fundraising')}</button>
              <button onClick={() => scrollToSection('blog')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('blog')}</button>
              <button onClick={() => scrollToSection('team')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('team')}</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('contact')}</button>
              
              {/* Language Selector */}
              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="text-gray-800 bg-transparent border-none focus:outline-none focus:ring-0"
              >
                <option value="en">🇺🇸 EN</option>
                <option value="es">🇪🇸 ES</option>
                <option value="zh">🇨🇳 中文</option>
              </select>

              {user ? (
                <div className="flex items-center space-x-4">
                  <button onClick={() => scrollToSection('dashboard')} className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">{t('dashboard')}</button>
                  <span className="text-gray-800 text-sm">{t('welcome')}, {user.full_name}</span>
                  <button
                    onClick={logout}
                    className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600"
                  >
                    {t('logout')}
                  </button>
                </div>
              ) : (
                <button onClick={() => scrollToSection('auth')} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">
                  {t('login')}
                </button>
              )}
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-800 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
            >
              ☰
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t max-h-96 overflow-y-auto">
            <button onClick={() => scrollToSection('home')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('home')}</button>
            <button onClick={() => scrollToSection('about')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('about')}</button>
            <button onClick={() => scrollToSection('technology')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('technology')}</button>
            <button onClick={() => scrollToSection('services')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('services')}</button>
            <button onClick={() => scrollToSection('success-stories')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('successStories')}</button>
            <button onClick={() => scrollToSection('enhanced-fundraising')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('fundraising')}</button>
            <button onClick={() => scrollToSection('blog')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('blog')}</button>
            <button onClick={() => scrollToSection('team')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('team')}</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('contact')}</button>
            
            {/* Language Selector */}
            <div className="px-3 py-2">
              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="w-full text-gray-800 bg-gray-50 border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="en">🇺🇸 English</option>
                <option value="es">🇪🇸 Español</option>
                <option value="zh">🇨🇳 中文</option>
              </select>
            </div>

            {user ? (
              <div className="px-3 py-2">
                <button onClick={() => scrollToSection('dashboard')} className="text-gray-800 hover:text-blue-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left">{t('dashboard')}</button>
                <span className="text-gray-800 text-sm block mb-2">{t('welcome')}, {user.full_name}</span>
                <button
                  onClick={logout}
                  className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600"
                >
                  {t('logout')}
                </button>
              </div>
            ) : (
              <button onClick={() => scrollToSection('auth')} className="bg-blue-600 text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700 w-full text-left">
                {t('login')}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Fundraising Progress Bar */}
      <div className="bg-blue-50 px-4 py-2">
        <FundraisingProgress />
      </div>
    </nav>
  );
};

// Hero Section Component
const HeroSection = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1628939824352-baa1cdddeb6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxvcHRpY2FsJTIwY29tcHV0aW5nfGVufDB8fHxibHVlfDE3NTI4NTYyMjZ8MA&ixlib=rb-4.1.0&q=85)'
        }}
      />
      <div className="absolute inset-0 bg-blue-900 bg-opacity-70"></div>
      
      <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
          Revolutionary Optical Computing
        </h1>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-8">
          Crypto Wallet Recovery
        </h2>
        <p className="text-xl sm:text-2xl mb-8 max-w-4xl mx-auto">
          Harness the power of cutting-edge optical computing technology to recover lost cryptocurrency wallets, 
          seed phrases, and private keys at unprecedented speeds. The future of crypto recovery is here.
        </p>
        
        {/* Quick Introduction to Optical Computing */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8 max-w-3xl mx-auto">
          <h3 className="text-xl font-semibold mb-4">Why Optical Computing?</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-3xl mb-2">⚡</div>
              <div className="font-medium">Light Speed Processing</div>
              <div className="text-gray-200">Photons travel at the speed of light</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🔄</div>
              <div className="font-medium">Parallel Computing</div>
              <div className="text-gray-200">Process millions of calculations simultaneously</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🎯</div>
              <div className="font-medium">85% Success Rate</div>
              <div className="text-gray-200">Industry-leading recovery success</div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {user ? (
            <button
              onClick={() => scrollToSection('dashboard')}
              className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition duration-300"
            >
              Go to {t('dashboard')}
            </button>
          ) : (
            <button
              onClick={() => scrollToSection('auth')}
              className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition duration-300"
            >
              {t('startRecovery')}
            </button>
          )}
          <button
            onClick={() => scrollToSection('enhanced-fundraising')}
            className="inline-block bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition duration-300"
          >
            {t('supportMission')}
          </button>
        </div>
      </div>
    </section>
  );
};

// Success Stories Section Component
const SuccessStoriesSection = () => {
  const [stories, setStories] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    fetchSuccessStories();
  }, []);

  const fetchSuccessStories = async () => {
    try {
      const response = await axios.get(`${API}/success-stories`);
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching success stories:', error);
    }
  };

  return (
    <section id="success-stories" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('successStories')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real recoveries, real results. See how our optical computing technology has helped clients recover millions in cryptocurrency.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((story, index) => (
            <div key={story.id} className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{story.title}</h3>
                  {story.is_featured && (
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                      Featured
                    </span>
                  )}
                </div>
                
                <p className="text-gray-600 mb-4">{story.description}</p>
                
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {story.recovery_amount.toLocaleString()} {story.currency}
                      </div>
                      <div className="text-sm text-gray-600">Recovered Amount</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">{story.recovery_time}</div>
                      <div className="text-sm text-gray-600">Recovery Time</div>
                    </div>
                  </div>
                </div>
                
                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 mb-4">
                  "{story.testimonial}"
                </blockquote>
                
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">— {story.customer_initials}</div>
                  <div className="text-xs text-gray-500">Verified Customer</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Join thousands of satisfied customers who have recovered their cryptocurrency with OpticalRecover.
          </p>
          <button
            onClick={() => document.getElementById('auth').scrollIntoView({ behavior: 'smooth' })}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300"
          >
            {t('startRecovery')}
          </button>
        </div>
      </div>
    </section>
  );
};

// FAQ Section Component
const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600">
            Get answers to common questions about our optical computing recovery process.
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={faq.id} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition duration-300"
              >
                <span className="font-semibold text-gray-900">{faq.question}</span>
                <span className="text-gray-500">
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <div className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Still have questions? Our support team is here to help.
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300"
          >
            Contact Support
          </button>
        </div>
      </div>
    </section>
  );
};

// Team Section Component
const TeamSection = () => {
  const [team, setTeam] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  return (
    <section id="team" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('team')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Meet the world-class experts behind OpticalRecover's revolutionary technology.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, index) => (
            <div key={member.id} className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition duration-300">
              <div className="relative h-64">
                <img 
                  src={member.image_url || 'https://via.placeholder.com/400x400?text=Team+Member'}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-medium mb-3">{member.position}</p>
                <p className="text-gray-600 text-sm mb-4">{member.bio}</p>
                
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Expertise:</h4>
                  <div className="flex flex-wrap gap-1">
                    {member.expertise.map((skill, skillIndex) => (
                      <span key={skillIndex} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                
                {member.linkedin_url && (
                  <a
                    href={member.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    LinkedIn Profile →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Blog Section Component
const BlogSection = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
  };

  return (
    <section id="blog" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('blog')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay updated with the latest insights on cryptocurrency security, recovery techniques, and optical computing breakthroughs.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <div key={post.id} className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition duration-300">
              <div className="relative h-48">
                <img 
                  src={post.image_url || 'https://via.placeholder.com/800x400?text=Blog+Post'}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {post.category}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{post.title}</h3>
                <p className="text-gray-600 mb-4">{post.excerpt}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>By {post.author}</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
                
                <div className="flex flex-wrap gap-1 mb-4">
                  {post.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <button className="text-blue-600 hover:text-blue-800 font-medium">
                  {t('readMore')} →
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300">
            View All Posts
          </button>
        </div>
      </div>
    </section>
  );
};

// Partnerships Section Component
const PartnershipsSection = () => {
  const [partnerships, setPartnerships] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API}/partnerships`);
      setPartnerships(response.data);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
    }
  };

  return (
    <section id="partnerships" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('partnerships')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Collaborating with industry leaders to advance optical computing technology and cryptocurrency recovery.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {partnerships.map((partnership, index) => (
            <div key={partnership.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="flex items-center mb-4">
                <img 
                  src={partnership.logo_url || 'https://via.placeholder.com/200x100?text=Partner+Logo'}
                  alt={partnership.name}
                  className="h-16 w-auto mr-4"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{partnership.name}</h3>
                  <span className="text-sm text-blue-600 font-medium">{partnership.partnership_type}</span>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">{partnership.description}</p>
              
              {partnership.website_url && (
                <a
                  href={partnership.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Visit Website →
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section Component (Enhanced)
const AboutSection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="about" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{t('about')} OpticalRecover</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Pioneering the future of cryptocurrency recovery through revolutionary optical computing technology
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Our Mission</h3>
            <p className="text-gray-700 mb-4 md:mb-6 text-sm md:text-base">
              At OpticalRecover, we believe that lost cryptocurrency shouldn't stay lost forever. Our mission is to harness 
              the revolutionary power of optical computing to solve the most complex cryptographic challenges and recover 
              what was thought to be permanently lost.
            </p>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Our Vision</h3>
            <p className="text-gray-700 mb-4 md:mb-6 text-sm md:text-base">
              We envision a future where optical computing transforms the cryptocurrency recovery industry, enabling 
              lightning-fast processing of complex algorithms that would take traditional computers years to solve. 
              Our goal is to build the world's most advanced optical computing facility dedicated to crypto recovery.
            </p>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Why Optical Computing?</h3>
            <p className="text-gray-700 text-sm md:text-base">
              Optical computing processes information using photons instead of electrons, allowing for parallel processing 
              at the speed of light. This technology enables us to crack complex cryptographic problems exponentially faster 
              than traditional methods, revolutionizing the entire field of cryptocurrency recovery.
            </p>
          </div>
          
          <div className="relative order-1 lg:order-2">
            <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1644126445930-2c09a9207403?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxvcHRpY2FsJTIwY29tcHV0aW5nfGVufDB8fHxibHVlfDE3NTI4NTYyMjZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Optical Computing Technology"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mt-12 md:mt-16">
          <div className="text-center">
            <div className="bg-blue-600 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-6">
              <span className="text-white text-lg md:text-2xl">⚡</span>
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-4">Lightning Fast</h3>
            <p className="text-gray-600 text-xs md:text-sm">Optical computing processes at light speed, solving complex problems in minutes instead of years.</p>
          </div>
          
          <div className="text-center">
            <div className="bg-blue-600 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-6">
              <span className="text-white text-lg md:text-2xl">🔬</span>
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-4">Cutting Edge</h3>
            <p className="text-gray-600 text-xs md:text-sm">We're at the forefront of optical computing technology, pushing the boundaries of what's possible.</p>
          </div>
          
          <div className="text-center">
            <div className="bg-blue-600 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-6">
              <span className="text-white text-lg md:text-2xl">🎯</span>
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-4">85% Success Rate</h3>
            <p className="text-gray-600 text-xs md:text-sm">Industry-leading success rate in cryptocurrency recovery using optical computing.</p>
          </div>
          
          <div className="text-center">
            <div className="bg-blue-600 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-6">
              <span className="text-white text-lg md:text-2xl">🌍</span>
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-4">Global Impact</h3>
            <p className="text-gray-600 text-xs md:text-sm">Our technology will help millions of people recover billions of dollars in lost cryptocurrency.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const { t } = useLanguage();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitMessage('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitMessage('Error sending message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{t('contact')} Us</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to recover your cryptocurrency? Get in touch with our optical computing experts today.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8">Get in Touch</h3>
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-center">
                <div className="bg-blue-600 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mr-4">
                  <span className="text-white text-base md:text-lg">📧</span>
                </div>
                <div>
                  <p className="font-semibold text-sm md:text-base">Email</p>
                  <p className="text-gray-600 text-sm md:text-base">info@opticalrecover.com</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-blue-600 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mr-4">
                  <span className="text-white text-base md:text-lg">📞</span>
                </div>
                <div>
                  <p className="font-semibold text-sm md:text-base">Phone</p>
                  <p className="text-gray-600 text-sm md:text-base">+1 (555) 123-4567</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-blue-600 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mr-4">
                  <span className="text-white text-base md:text-lg">📍</span>
                </div>
                <div>
                  <p className="font-semibold text-sm md:text-base">Address</p>
                  <p className="text-gray-600 text-sm md:text-base">123 Quantum Drive<br />Silicon Valley, CA 94025</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-blue-600 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mr-4">
                  <span className="text-white text-base md:text-lg">⏰</span>
                </div>
                <div>
                  <p className="font-semibold text-sm md:text-base">Hours</p>
                  <p className="text-gray-600 text-sm md:text-base">Mon-Fri: 9AM-6PM PST<br />24/7 Emergency Support</p>
                </div>
              </div>
            </div>
            
            {/* Map placeholder */}
            <div className="mt-6 md:mt-8 bg-gray-200 rounded-lg h-48 md:h-64 flex items-center justify-center">
              <div className="text-center">
                <span className="text-3xl md:text-4xl text-gray-400">🗺️</span>
                <p className="text-gray-600 mt-2 text-sm md:text-base">Interactive Map</p>
                <p className="text-xs md:text-sm text-gray-500">Silicon Valley, CA</p>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                >
                  <option value="">Select a subject</option>
                  <option value="Recovery Request">Recovery Request</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Investment">Investment</option>
                  <option value="Press Inquiry">Press Inquiry</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  placeholder="Tell us about your recovery needs or inquiry..."
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition duration-300 disabled:opacity-50 text-sm md:text-base"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            
            {submitMessage && (
              <div className={`mt-4 p-4 rounded-md ${submitMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {submitMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Auth Section Component
const AuthSection = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { login, register } = useAuth();
  const { t } = useLanguage();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    if (isLogin) {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        setMessage('Login successful! Redirecting to dashboard...');
        setTimeout(() => {
          const element = document.getElementById('dashboard');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 1000);
      } else {
        setMessage(result.error);
      }
    } else {
      const result = await register(formData.email, formData.password, formData.full_name);
      if (result.success) {
        setMessage('Registration successful! Please login.');
        setIsLogin(true);
        setFormData({ email: '', password: '', full_name: '' });
      } else {
        setMessage(result.error);
      }
    }
    
    setIsLoading(false);
  };

  return (
    <section id="auth" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-xl p-6 md:p-8">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {isLogin ? t('login') : t('register')}
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            {isLogin ? 'Access your dashboard' : 'Create your account'}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          {!isLogin && (
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              />
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition duration-300 disabled:opacity-50 text-sm md:text-base"
          >
            {isLoading ? 'Processing...' : (isLogin ? t('login') : t('register'))}
          </button>
        </form>
        
        {message && (
          <div className={`mt-4 p-4 rounded-md ${message.includes('Error') || message.includes('failed') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {message}
          </div>
        )}
        
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setMessage('');
              setFormData({ email: '', password: '', full_name: '' });
            }}
            className="text-blue-600 hover:text-blue-800 text-sm md:text-base"
          >
            {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
          </button>
        </div>
      </div>
    </section>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user } = useAuth();
  const [recoveryRequests, setRecoveryRequests] = useState([]);
  const [donations, setDonations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    wallet_type: '',
    service_type: '',
    description: '',
    priority: 'standard'
  });
  const { t } = useLanguage();

  useEffect(() => {
    if (user) {
      fetchRecoveryRequests();
      fetchDonations();
    }
  }, [user]);

  const fetchRecoveryRequests = async () => {
    try {
      const response = await axios.get(`${API}/recovery/requests`);
      setRecoveryRequests(response.data);
    } catch (error) {
      console.error('Error fetching recovery requests:', error);
    }
  };

  const fetchDonations = async () => {
    try {
      const response = await axios.get(`${API}/donations/user`);
      setDonations(response.data);
    } catch (error) {
      console.error('Error fetching donations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/recovery/request`, formData);
      setFormData({ wallet_type: '', service_type: '', description: '', priority: 'standard' });
      fetchRecoveryRequests();
    } catch (error) {
      console.error('Error creating recovery request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!user) {
    return <Navigate to="#auth" replace />;
  }

  return (
    <section id="dashboard" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('welcome')}, {user.full_name}
          </h2>
          <p className="text-lg md:text-xl text-gray-600">Manage your recovery requests and track your contributions</p>
        </div>
        
        {/* User Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 text-center">
            <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-2">{recoveryRequests.length}</div>
            <div className="text-gray-600 text-sm md:text-base">Recovery Requests</div>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 text-center">
            <div className="text-2xl md:text-3xl font-bold text-green-600 mb-2">${user.total_donated?.toLocaleString() || 0}</div>
            <div className="text-gray-600 text-sm md:text-base">Total Donated</div>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 text-center">
            <div className="text-2xl md:text-3xl font-bold text-purple-600 mb-2">{donations.length}</div>
            <div className="text-gray-600 text-sm md:text-base">Donations Made</div>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* New Recovery Request Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
              <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">New Recovery Request</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="wallet_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Wallet Type
                  </label>
                  <select
                    id="wallet_type"
                    name="wallet_type"
                    value={formData.wallet_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  >
                    <option value="">Select wallet type</option>
                    <option value="bitcoin">Bitcoin</option>
                    <option value="ethereum">Ethereum</option>
                    <option value="litecoin">Litecoin</option>
                    <option value="dogecoin">Dogecoin</option>
                    <option value="hardware">Hardware Wallet</option>
                    <option value="mobile">Mobile Wallet</option>
                    <option value="exchange">Exchange Account</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="service_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type
                  </label>
                  <select
                    id="service_type"
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  >
                    <option value="">Select service type</option>
                    <option value="private_key">Private Key Recovery</option>
                    <option value="seed_phrase">Seed Phrase Restoration</option>
                    <option value="password">Password Recovery</option>
                    <option value="wallet_file">Wallet File Recovery</option>
                    <option value="blockchain_forensics">Blockchain Forensics</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  >
                    <option value="standard">Standard</option>
                    <option value="high">High Priority (+50%)</option>
                    <option value="urgent">Urgent (+100%)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    required
                    placeholder="Describe your recovery needs in detail..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition duration-300 disabled:opacity-50 text-sm md:text-base"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
          
          {/* Recovery Requests List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 mb-6 md:mb-8">
              <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Your Recovery Requests</h3>
              {recoveryRequests.length === 0 ? (
                <p className="text-gray-600 text-sm md:text-base">No recovery requests yet. Submit your first request!</p>
              ) : (
                <div className="space-y-4">
                  {recoveryRequests.map((request) => (
                    <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold capitalize text-sm md:text-base">{request.wallet_type} - {request.service_type}</h4>
                          <p className="text-xs md:text-sm text-gray-600">Priority: {request.priority}</p>
                        </div>
                        <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm ${
                          request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          request.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {request.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2 text-sm md:text-base">{request.description}</p>
                      <div className="flex justify-between text-xs md:text-sm text-gray-500">
                        <span>Submitted: {new Date(request.created_at).toLocaleDateString()}</span>
                        <span>Updated: {new Date(request.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Donation History */}
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
              <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Your Donation History</h3>
              {donations.length === 0 ? (
                <p className="text-gray-600 text-sm md:text-base">No donations yet. Consider supporting our mission!</p>
              ) : (
                <div className="space-y-4">
                  {donations.map((donation) => (
                    <div key={donation.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-sm md:text-base">${donation.amount.toLocaleString()}</h4>
                          <p className="text-xs md:text-sm text-blue-600">{donation.tier}</p>
                        </div>
                        <span className="text-xs md:text-sm text-gray-500">
                          {new Date(donation.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {donation.message && (
                        <p className="text-gray-600 text-xs md:text-sm italic">"{donation.message}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Technology Section Component
const TechnologySection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="technology" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Optical Computing {t('technology')}</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding the revolutionary technology that powers our crypto recovery solutions
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-12 md:mb-16">
          <div className="order-2 lg:order-1">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">How Optical Computing Works</h3>
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="text-blue-600 text-sm font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Photonic Processing</h4>
                  <p className="text-gray-700 text-sm md:text-base">Uses light photons instead of electrons to process information, enabling parallel computation at light speed.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="text-blue-600 text-sm font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Quantum Interference</h4>
                  <p className="text-gray-700 text-sm md:text-base">Leverages quantum properties of light for complex calculations and pattern recognition.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="text-blue-600 text-sm font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Massively Parallel</h4>
                  <p className="text-gray-700 text-sm md:text-base">Processes millions of calculations simultaneously, exponentially faster than traditional computers.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative order-1 lg:order-2">
            <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1636979648933-6d06b1ce9ad7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxxdWFudHVtJTIwcHJvY2Vzc29yc3xlbnwwfHx8Ymx1ZXwxNzUyODU2MjM0fDA&ixlib=rb-4.1.0&q=85"
                alt="Quantum Processor"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-6 md:p-8 mb-12 md:mb-16">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 text-center">Why Optical Computing is Superior</h3>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 md:mb-4">Traditional Computing</h4>
              <ul className="space-y-2 text-gray-700 text-sm md:text-base">
                <li>• Sequential processing</li>
                <li>• Limited by electron speed</li>
                <li>• Heat generation issues</li>
                <li>• Power consumption constraints</li>
                <li>• Years to solve complex problems</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 md:mb-4">Optical Computing</h4>
              <ul className="space-y-2 text-gray-700 text-sm md:text-base">
                <li>• Parallel processing at light speed</li>
                <li>• Photonic information transfer</li>
                <li>• Minimal heat generation</li>
                <li>• Energy efficient operations</li>
                <li>• Minutes to solve complex problems</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Our Optical Computing Applications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
              <div className="relative w-full h-40 md:h-48 rounded-lg overflow-hidden mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1618849821300-5f216d5e4937?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxvcHRpY2FsJTIwY29tcHV0aW5nfGVufDB8fHxibHVlfDE3NTI4NTYyMjZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Cryptographic Analysis"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Cryptographic Analysis</h4>
              <p className="text-gray-600 text-sm md:text-base">Breaking complex encryption algorithms and recovering lost private keys.</p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
              <div className="relative w-full h-40 md:h-48 rounded-lg overflow-hidden mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1611759386222-9ac905b251d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxxdWFudHVtJTIwcHJvY2Vzc29yc3xlbnwwfHx8Ymx1ZXwxNzUyODU2MjM0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Pattern Recognition"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Pattern Recognition</h4>
              <p className="text-gray-600 text-sm md:text-base">Identifying patterns in damaged or incomplete seed phrases and wallet data.</p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
              <div className="relative w-full h-40 md:h-48 rounded-lg overflow-hidden mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1636979648933-6d06b1ce9ad7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxxdWFudHVtJTIwcHJvY2Vzc29yc3xlbnwwfHx8Ymx1ZXwxNzUyODU2MjM0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Quantum Simulation"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Quantum Simulation</h4>
              <p className="text-gray-600 text-sm md:text-base">Simulating quantum processes for advanced cryptographic recovery techniques.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section Component (Enhanced)
const ServicesSection = () => {
  const { t } = useLanguage();
  
  const services = [
    {
      icon: "🔑",
      title: "Private Key Recovery",
      description: "Recover lost or corrupted private keys using advanced optical computing algorithms",
      features: ["Damaged key reconstruction", "Brute force acceleration", "Pattern analysis"],
      image: "https://images.unsplash.com/photo-1618849821300-5f216d5e4937?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxvcHRpY2FsJTIwY29tcHV0aW5nfGVufDB8fHxibHVlfDE3NTI4NTYyMjZ8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: "🌱",
      title: "Seed Phrase Restoration",
      description: "Restore incomplete or damaged seed phrases with our optical pattern recognition",
      features: ["Missing word recovery", "Order reconstruction", "Checksum validation"],
      image: "https://images.unsplash.com/photo-1611759386222-9ac905b251d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxxdWFudHVtJTIwcHJvY2Vzc29yc3xlbnwwfHx8Ymx1ZXwxNzUyODU2MjM0fDA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: "🔐",
      title: "Password Recovery",
      description: "Crack complex wallet passwords using quantum-enhanced optical computing power",
      features: ["Dictionary attacks", "Pattern-based recovery", "Hybrid algorithms"],
      image: "https://images.unsplash.com/photo-1636979648933-6d06b1ce9ad7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxxdWFudHVtJTIwcHJvY2Vzc29yc3xlbnwwfHx8Ymx1ZXwxNzUyODU2MjM0fDA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: "💾",
      title: "Wallet File Recovery",
      description: "Recover data from corrupted or damaged wallet files using optical forensics",
      features: ["File reconstruction", "Data extraction", "Format recovery"],
      image: "https://images.unsplash.com/photo-1644126445930-2c09a9207403?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxvcHRpY2FsJTIwY29tcHV0aW5nfGVufDB8fHxibHVlfDE3NTI4NTYyMjZ8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: "📱",
      title: "Mobile Wallet Recovery",
      description: "Specialized recovery for mobile wallets and hardware device failures",
      features: ["Device forensics", "App data recovery", "Backup restoration"],
      image: "https://images.unsplash.com/photo-1628939824352-baa1cdddeb6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxvcHRpY2FsJTIwY29tcHV0aW5nfGVufDB8fHxibHVlfDE3NTI4NTYyMjZ8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: "🔍",
      title: "Blockchain Forensics",
      description: "Advanced blockchain analysis and transaction tracing using optical computing",
      features: ["Transaction analysis", "Address clustering", "Fund tracing"],
      image: "https://images.unsplash.com/photo-1611759386222-9ac905b251d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxxdWFudHVtJTIwcHJvY2Vzc29yc3xlbnwwfHx8Ymx1ZXwxNzUyODU2MjM0fDA&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <section id="services" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our {t('services')}</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive cryptocurrency recovery solutions powered by cutting-edge optical computing technology
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition duration-300">
              <div className="relative h-40 md:h-48">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
                <div className="absolute top-3 left-3 md:top-4 md:left-4 bg-white rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center">
                  <span className="text-2xl md:text-3xl">{service.icon}</span>
                </div>
              </div>
              
              <div className="p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">{service.description}</p>
                
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 text-sm md:text-base">Key Features:</h4>
                  <ul className="text-xs md:text-sm text-gray-600">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
                
                <button className="mt-4 md:mt-6 w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300 text-sm md:text-base">
                  {t('learnMore')}
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8 md:mt-12">
          <p className="text-gray-600 mb-4 text-sm md:text-base">
            Don't see your specific recovery need? We offer custom solutions using our optical computing technology.
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-blue-600 text-white px-6 md:px-8 py-2 md:py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 text-sm md:text-base"
          >
            {t('contactUs')}
          </button>
        </div>
      </div>
    </section>
  );
};

// Enhanced Fundraising Section Component
const EnhancedFundraisingSection = () => {
  const [stats, setStats] = useState({
    total_raised: 0,
    goal_amount: 500000,
    progress_percentage: 0,
    total_donors: 0,
    recent_donations: []
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit_card');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });

  const predefinedAmounts = [25, 50, 100, 250, 500, 1000];

  useEffect(() => {
    fetchFundraisingStats();
    fetchPaymentMethods();
  }, []);

  const fetchFundraisingStats = async () => {
    try {
      const response = await axios.get(`${API}/donations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching fundraising stats:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/donations/payment-methods`);
      setPaymentMethods(response.data.methods);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleAmountSelect = (amount) => {
    setDonationAmount(amount.toString());
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setDonationAmount('');
  };

  const getFinalAmount = () => {
    return customAmount || donationAmount;
  };

  const handleDonation = async (e) => {
    e.preventDefault();
    const finalAmount = getFinalAmount();
    
    if (!finalAmount || !donorName || !donorEmail) {
      setSubmitMessage('Please fill in all required fields.');
      return;
    }

    if (parseFloat(finalAmount) < 1) {
      setSubmitMessage('Minimum donation amount is $1.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Process payment first
      const paymentResponse = await axios.post(`${API}/donations/process-payment`, {
        payment_method: selectedPaymentMethod,
        amount: parseFloat(finalAmount),
        card_details: selectedPaymentMethod === 'credit_card' ? cardDetails : null
      });

      if (paymentResponse.data.status === 'success') {
        // Create donation record
        await axios.post(`${API}/donations`, {
          amount: parseFloat(finalAmount),
          currency: 'USD',
          donor_name: donorName,
          donor_email: donorEmail,
          message: message,
          is_anonymous: isAnonymous,
          payment_method: selectedPaymentMethod
        });

        setSubmitMessage('Thank you for your donation! Your support helps us build the future of crypto recovery.');
        
        // Reset form
        setDonationAmount('');
        setCustomAmount('');
        setDonorName('');
        setDonorEmail('');
        setMessage('');
        setIsAnonymous(false);
        setCardDetails({
          cardNumber: '',
          expiryDate: '',
          cvv: '',
          cardholderName: ''
        });
        
        // Refresh stats
        fetchFundraisingStats();
        
        // Update top progress bar
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSubmitMessage('Payment processing failed. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Error processing donation. Please try again.');
      console.error('Donation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDonationTier = (amount) => {
    if (amount >= 1000) return 'Tech Champion';
    if (amount >= 500) return 'Early Adopter';
    if (amount >= 100) return 'Supporter';
    return 'Contributor';
  };

  const getSelectedPaymentMethod = () => {
    return paymentMethods.find(method => method.id === selectedPaymentMethod);
  };

  return (
    <section id="enhanced-fundraising" className="py-12 md:py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Support Our Mission</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Help us build the world's most advanced optical computing facility for cryptocurrency recovery
          </p>
        </div>
        
        {/* Enhanced Progress Display */}
        <div className="bg-white rounded-lg shadow-2xl p-6 md:p-8 mb-8 md:mb-12">
          <div className="text-center mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Fundraising Progress</h3>
            <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">
              ${stats.total_raised.toLocaleString()}
            </div>
            <div className="text-base md:text-lg text-gray-600 mb-4 md:mb-6">
              of ${stats.goal_amount.toLocaleString()} goal
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-4 md:h-6 mb-4 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-1000 ease-out relative"
                style={{ width: `${Math.min(stats.progress_percentage, 100)}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
              <div>
                <div className="text-xl md:text-2xl font-bold text-green-600">{stats.progress_percentage.toFixed(1)}%</div>
                <div className="text-xs md:text-sm text-gray-600">Complete</div>
              </div>
              <div>
                <div className="text-xl md:text-2xl font-bold text-blue-600">{stats.total_donors}</div>
                <div className="text-xs md:text-sm text-gray-600">Contributors</div>
              </div>
              <div>
                <div className="text-xl md:text-2xl font-bold text-purple-600">
                  ${(stats.goal_amount - stats.total_raised).toLocaleString()}
                </div>
                <div className="text-xs md:text-sm text-gray-600">Remaining</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced Donation Form */}
        <div className="bg-white rounded-lg shadow-2xl p-6 md:p-8 max-w-2xl mx-auto">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Make a Donation</h3>
          
          <form onSubmit={handleDonation} className="space-y-4 md:space-y-6">
            {/* Amount Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 md:mb-4">
                Select Donation Amount
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4">
                {predefinedAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => handleAmountSelect(amount)}
                    className={`p-2 md:p-3 rounded-lg border-2 transition-all duration-200 ${
                      donationAmount === amount.toString()
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="text-base md:text-lg font-semibold">${amount}</div>
                    <div className="text-xs text-gray-500">{getDonationTier(amount)}</div>
                  </button>
                ))}
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="number"
                  min="1"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  placeholder="Custom amount"
                />
              </div>
              
              {(getFinalAmount()) && (
                <div className="mt-2 text-sm text-blue-600">
                  Tier: {getDonationTier(parseFloat(getFinalAmount()))}
                </div>
              )}
            </div>
            
            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 md:mb-4">
                Payment Method
              </label>
              <div className="grid grid-cols-1 gap-2 md:gap-3">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedPaymentMethod(method.id)}
                    className={`p-3 md:p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                      selectedPaymentMethod === method.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-xl md:text-2xl">{method.icon}</span>
                        <div>
                          <div className="font-semibold text-sm md:text-base">{method.name}</div>
                          <div className="text-xs md:text-sm text-gray-500">{method.description}</div>
                        </div>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        selectedPaymentMethod === method.id
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedPaymentMethod === method.id && (
                          <div className="w-full h-full rounded-full bg-white transform scale-50"></div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Payment Details */}
            {selectedPaymentMethod === 'credit_card' && (
              <div className="space-y-3 md:space-y-4">
                <h4 className="font-semibold text-gray-900">Credit Card Details</h4>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={cardDetails.cardNumber}
                      onChange={(e) => setCardDetails({...cardDetails, cardNumber: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                      placeholder="Card Number"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={cardDetails.expiryDate}
                      onChange={(e) => setCardDetails({...cardDetails, expiryDate: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                      placeholder="MM/YY"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={cardDetails.cvv}
                      onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                      placeholder="CVV"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={cardDetails.cardholderName}
                      onChange={(e) => setCardDetails({...cardDetails, cardholderName: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                      placeholder="Cardholder Name"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Donor Information */}
            <div className="space-y-3 md:space-y-4">
              <h4 className="font-semibold text-gray-900">Donor Information</h4>
              <div>
                <input
                  type="text"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  placeholder="Full Name"
                  required
                />
              </div>
              
              <div>
                <input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  placeholder="Email Address"
                  required
                />
              </div>
              
              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  placeholder="Message (Optional)"
                ></textarea>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="anonymous" className="text-sm text-gray-700">
                  Make this donation anonymous
                </label>
              </div>
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !getFinalAmount()}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 md:py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                `Donate $${getFinalAmount() || '0'} via ${getSelectedPaymentMethod()?.name || 'Selected Method'}`
              )}
            </button>
          </form>
          
          {submitMessage && (
            <div className={`mt-4 md:mt-6 p-4 rounded-lg ${
              submitMessage.includes('Error') || submitMessage.includes('failed') 
                ? 'bg-red-100 text-red-700 border border-red-200' 
                : 'bg-green-100 text-green-700 border border-green-200'
            }`}>
              {submitMessage}
            </div>
          )}
        </div>
        
        {/* Recent Donations */}
        {stats.recent_donations.length > 0 && (
          <div className="mt-8 md:mt-12 bg-white rounded-lg shadow-xl p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Recent Contributors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.recent_donations.map((donation, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm md:text-base">{donation.donor_name}</h4>
                    <span className="text-green-600 font-bold text-sm md:text-base">${donation.amount.toLocaleString()}</span>
                  </div>
                  <p className="text-xs md:text-sm text-blue-600 mb-1">{donation.tier}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(donation.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
          {/* Company Info */}
          <div className="text-center md:text-left">
            <span className="text-lg font-bold text-blue-400">OpticalRecover</span>
            <p className="text-gray-400 text-xs mt-1">
              Revolutionary crypto wallet recovery technology
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center space-x-4 text-xs">
            <button onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white">{t('about')}</button>
            <button onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white">{t('services')}</button>
            <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white">{t('contact')}</button>
          </div>

          {/* Copyright */}
          <div className="text-center md:text-right">
            <p className="text-gray-400 text-xs">
              © {currentYear} OpticalRecover. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
const Home = () => {
  return (
    <div className="min-h-screen">
      <TopProgressBar />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <TechnologySection />
      <ServicesSection />
      <EnhancedFundraisingSection />
      <SuccessStoriesSection />
      <FAQSection />
      <TeamSection />
      <BlogSection />
      <PartnershipsSection />
      <ContactSection />
      <AuthSection />
      <Dashboard />
      <Footer />
      <Chatbot />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Home />} />
            </Routes>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;